import React from 'react';
import { Calendar, DollarSign, Plane, Snowflake, CreditCard, Shield, PiggyBank, LogOut, HelpCircle, BookOpen, Settings, BarChart, Wallet, Heart, Brain } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { signOut } from '../lib/firebase';

interface NavbarProps {
  onViewChange: (view: string) => void;
  currentView: string;
}

export default function Navbar({ onViewChange, currentView }: NavbarProps) {
  const { user } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const menuItems = [
    { icon: Calendar, label: 'Calendar', id: 'calendar' },
    { icon: Wallet, label: 'Budget Buckets', id: 'buckets' },
    { icon: DollarSign, label: 'Upcoming Paydays', id: 'upcoming-paydays' },
    { icon: PiggyBank, label: 'Savings', id: 'savings' },
    { icon: BarChart, label: 'Spending Analytics', id: 'analytics' },
    { icon: Plane, label: 'Vacation Planning', id: 'vacation' },
    { icon: Snowflake, label: 'Debt Snowball', id: 'debt' },
    { icon: CreditCard, label: 'Subscriptions', id: 'subscriptions' },
    { icon: Brain, label: 'Can I Afford It?', id: 'affordit' },
    { icon: BookOpen, label: 'Resources', id: 'resources' },
    { icon: HelpCircle, label: 'Support', id: 'support' },
    ...(user?.isAdmin ? [{ icon: Shield, label: 'Admin', id: 'admin' }] : []),
    { icon: Settings, label: 'Settings', id: 'settings' }
  ];

  return (
    <div className="w-64 glass-dark min-h-screen flex flex-col relative overflow-hidden">
      {/* Animated background effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-blue-900/30 to-slate-900/50 pointer-events-none"></div>
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>

      {/* Logo */}
      <div className="relative p-6 border-b border-slate-700/50">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-lg"></div>
            <img
              src="https://cdn.buymeacoffee.com/uploads/profile_pictures/2024/11/7GWCqqRG5XR4Olys.png@300w_0e.webp"
              alt="ClearCash Logo"
              className="relative h-10 w-10 rounded-full ring-2 ring-blue-400/50"
            />
          </div>
          <div>
            <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">ClearCash</span>
            <p className="text-xs text-slate-400">Financial Control</p>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="relative flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
        <div className="space-y-1 px-3">
          {menuItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => onViewChange(item.id)}
              className={`group w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                currentView === item.id
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-500/50 scale-105'
                  : 'text-slate-300 hover:bg-slate-800/50 hover:text-white hover:translate-x-1'
              }`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <item.icon className={`h-5 w-5 mr-3 transition-transform duration-300 ${
                currentView === item.id ? 'scale-110' : 'group-hover:scale-110'
              }`} />
              <span className="flex-1 text-left">{item.label}</span>
              {currentView === item.id && (
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Support & Logout Section */}
      <div className="relative p-4 border-t border-slate-700/50 space-y-2">
        <button
          onClick={() => onViewChange('donate')}
          className={`w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 hover:translate-x-1 group ${
            currentView === 'donate'
              ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg shadow-red-500/50'
              : 'text-slate-300 hover:bg-gradient-to-r hover:from-red-600/20 hover:to-pink-600/20 hover:text-red-400'
          }`}
        >
          <Heart className="h-5 w-5 mr-3 transition-transform duration-300 group-hover:scale-110" />
          <span>Support Project</span>
        </button>

        <button
          onClick={handleLogout}
          className="w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium text-slate-300 hover:bg-red-600/20 hover:text-red-400 transition-all duration-300 hover:translate-x-1 group"
        >
          <LogOut className="h-5 w-5 mr-3 transition-transform duration-300 group-hover:scale-110" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}